import { MyBookingCredentials } from '@/api/MyBookingCredentials/myBookingCredentials.interfaces';
import { HotelWidgetData } from '@/components/myBookingPage/Sections/Widgets/Hotel/HotelWidget.interfaces';
import { TripWidgetData } from '@/components/myBookingPage/Sections/Widgets/Trip/TripWidget.interfaces';
import { MyBookingCustomStatus } from '@/store/myBooking/types';
import { BookingProductCombination } from '@shared/interfaces/productCombination';
import { TransferJourneyType } from '@/helpers/transfer/transfer.interfaces';

export interface MyBookingEndPoint {
    GetBooking(params: MyBookingCredentials): Promise<any>;
}

export interface MyBooking extends MyBookingExtras {
    bookingId: string;
    bookingNumber: string;
    password: string;
    companyId: number;
    created: string;
    status: MyBookingStatus;
    customerServiceMessages: CustomerServiceMessage[];
    importantInformation: ImportantInformation[];
    links: Links;
    payment: Payment | null;
    canDisplayPaymentReminder: boolean;
    products: AnyBookingProduct[];
    coupons: Coupon[] | null;
    cancellationPolicies: BookingCancellationPolicy[];
}

interface MyBookingExtras {
    productCombination: BookingProductCombination | null;
    customStatus: MyBookingCustomStatus | null;
    expired?: boolean;
    spotlights?: MyBookingSpotlight[];
}

export type MyBookingSpotlight =
    | MyBookingSpotlightUpsells
    | MyBookingSpotlightHotelWidget
    | MyBookingSpotlightRentalCarWidget
    | MyBookingSpotlightTripWidget;

interface MyBookingSpotlightBase {
    position: number;
    enabled: boolean;
}

interface MyBookingSpotlightUpsells extends MyBookingSpotlightBase {
    type: SpotlightType.Upsells;
}

export interface MyBookingSpotlightHotelWidget extends MyBookingSpotlightBase {
    type: SpotlightType.HotelWidget;
    data: HotelWidgetData;
}

interface MyBookingSpotlightRentalCarWidget extends MyBookingSpotlightBase {
    type: SpotlightType.RentalCarWidget;
}

export interface MyBookingSpotlightTripWidget extends MyBookingSpotlightBase {
    type: SpotlightType.TripWidget;
    data: TripWidgetData;
}

export enum SpotlightType {
    Upsells = 'Upsells',
    HotelWidget = 'HotelWidget',
    RentalCarWidget = 'RentalCarWidget',
    TripWidget = 'TripWidget',
}

export interface Money {
    amount: number;
    currency: string;
}

export interface Coupon {
    type: CouponType;
    code: string;
    amount: Money;
    termsAmount: Money;
    validFor: ProductType[];
    expirationDate: string | Date;
}

export enum CouponType {
    Flight = 'Flight',
    Hotel = 'Hotel',
}

export interface CustomerServiceMessage {
    message: string;
    date: string | null;
    productId: string | number | null;
}

export interface Payment {
    invoice: InvoiceType | null;
    usedPaymentMethods: UsedPaymentMethod[] | null;
    closeToDeparture: boolean;
    downPaymentDueTime: string | null;
    paymentDueTime: string | null;
    commision: Money | null;
    amountAlreadyPaid: Money | null;
    remainingToBePaid: Money | null;
    remainingDownPaymentToBePaid: Money | null;
}

export interface Links {
    bookingConfirmation: string | null;
    cancellation: string | null;
    giftCard: string | null;
    termsAndCondition: string | null;
    sembok: string | null;
    voucher: {
        url: string;
        isMultiTicket: boolean;
        bookingOnlyHasGiftCertificate: boolean;
    } | null;
}

export interface UsedPaymentMethod {
    type: PaymentMethodType;
    method: string | null;
    amount: Money | null;
    date: string | null;
}

export interface ImportantInformation {
    type: InformationType;
    url?: string | null;
    productId?: number | null;
}

export enum MyBookingStatus {
    NotFinished = 'NotFinished',
    Completed = 'Completed',
    Cancelled = 'Cancelled',
    AwaitingOk = 'AwaitingOk',
    AwaitingOkFailedProduct = 'AwaitingOkFailedProduct',
    AwaitingOkVerification = 'AwaitingOkVerification',
    Aborted = 'Aborted',
}

export enum InformationType {
    ETAProgram = 'ETAProgram',
    VisaWaiverProgramWarning = 'VisaWaiverProgramWarning',
    UKVisaWarning = 'UKVisaWarning',
    ThailandArrivalCardWarning = 'ThailandArrivalCardWarning',
}

export enum PaymentMethodType {
    Adyen = 'Adyen',
    AIP = 'AIP',
    BUSINESS_TRAVEL = 'BUSINESS_TRAVEL',
    Captum = 'Captum',
    CaptumCheckout = 'CaptumCheckout',
    Dibs = 'Dibs',
    DibsD2 = 'DibsD2',
    GC_AIP = 'GC_AIP',
    GC_AXAPTA = 'GC_AXAPTA',
    GC_DTF_OLD = 'GC_DTF_OLD',
    GC_SLTG = 'GC_SLTG',
    Ikano = 'Ikano',
    Klarna = 'Klarna',
    Morebox = 'Morebox',
    Offline = 'Offline',
    PBS = 'PBS',
    SLGIFTCARD = 'SLGIFTCARD',
    SLINVOICE = 'SLINVOICE',
    LeaBank = 'LeaBank',
}

enum InvoiceType {
    Email = 'Email',
    PaperInvoice = 'PaperInvoice',
    PaperInvoiceFree = 'PaperInvoiceFree',
    WillNotBeSent = 'WillNotBeSent',
    File = 'File',
    NextEmailInvoiceWillNotBeSent = 'NextEmailInvoiceWillNotBeSent',
}

export type AnyBookingAddonProduct =
    | CancellationInsuranceProduct
    | TravelInsuranceProduct
    | ServicePackageProduct
    | ServicePackageAccommodationProduct
    | BaggageInsuranceProduct;

export type AnyBookingProduct =
    | HotelProduct
    | FerryProduct
    | FlightProduct
    | ActivityProduct
    | GolfProduct
    | TransferProduct
    | AnyBookingAddonProduct
    | CustomBaggageProduct
    | CustomSeatingProduct;

export type MainBookingProduct =
    | HotelProduct
    | FerryProduct
    | FlightProduct
    | ActivityProduct;

export enum ProductType {
    Hotel = 'Hotel',
    Flight = 'Flight',
    Ferry = 'Ferry',
    Golf = 'Golf',
    Activity = 'Activity',
    Transfer = 'Transfer',
    CustomBaggage = 'CustomBaggage',
    CustomSeating = 'CustomSeating',
    ServicePackageOld = 'ServicePackage',
    ServicePackageBasic = 'ServicePackageBasic',
    ServicePackagePremium = 'ServicePackagePremium',
    ServicePackageAccommodation = 'ServicePackageAccommodation',
    BaggageInsurance = 'BaggageInsurance',
    CancellationInsurance = 'CancellationInsurance',
    GenericProduct = 'GenericProduct',
}

interface Pax {
    title: number | null;
    firstName: string | null;
    lastName: string | null;
    dateOfBirth: string | null;
    nationality: PaxNationality | null;
    passportNumber?: string | null;
    passportExpirationDate?: string | null;
}

interface PaxNationality {
    countryCode: string;
    nationalityName: string;
}

export interface HotelProduct {
    type: ProductType.Hotel;
    hotelCode: string;
    hotelName: string;
    imageUrl: string;
    url: string;
    fromDate: string;
    toDate: string;
    checkInHours: string | null;
    checkOutHours: string | null;
    address: HotelAddress | null;
    rooms: HotelRoom[];
}

interface HotelAddress {
    streetAddress: string | null;
    phone: string | null;
    city: string | null;
    country: string | null;
    zipCode: string | null;
}

export interface HotelRoom {
    productId: number;
    roomDescription: string;
    adults: number;
    children: number;
    accommodationNotes: string | null;
}

export interface GolfProduct {
    type: ProductType.Golf;
    golfCode: string;
    golfName: string;
    imageUrl: string;
    fromDate: string;
    toDate: string;
    address: GolfAddress | null;
    tickets: GolfTicket[];
}
interface GolfAddress {
    streetAddress: string | null;
    phone: string | null;
    city: string | null;
    country: string | null;
    zipCode: string | null;
}

export interface GolfTicket {
    productId: number;
    ticketDescription: string;
    adults: number;
    children: number;
}

export interface FerryProduct {
    type: ProductType.Ferry;
    productId: number;
    pax: FerryPax[];
    legs: FerryLeg[];
}

export interface FerryLeg {
    segments: FerrySegment[];
}

// TODO: use LocationName and CountryName when they're sent back from booking-api
export interface FerrySegment {
    departureDate: string;
    arrivalDate: string;
    legName: string;
    ferryName: string | null;
    vehicle: string | null;
    vehicleRegNo: string | null;
    accommodations: FerryAccommodation[];
    meals: FerryAddon[];
    misc: FerryAddon[];
    pets: FerryAddon[];
    lounges: FerryAddon[];
    departurePortCode: string | null;
    departurePortName?: string | null;
    departurePortLocationName?: string | null;
    //departureLocationName?: string | null;
    //departureCountryName?: string | null;
    arrivalPortCode: string | null;
    arrivalPortName?: string | null;
    arrivalPortLocationName?: string | null;
    //arrivalLocationName?: string | null;
    //arrivalCountryName?: string | null;
    operatingCarrierLogoUrl?: string | null;
}

export interface FerryPax extends Pax {
    specialNeedsOption: number | null;
}

export interface FerryAddon {
    title: string;
    quantity: number;
}

export interface FerryAccommodation {
    title: string;
    quantity: number;
    type: FerryAccommodationType;
}

export enum FerryAccommodationType {
    Seat = 'Seat',
    Cabin = 'Cabin',
}

export interface FlightProduct {
    type: ProductType.Flight;
    ticket: FlightTicket;
    pax: FlightPax[];
    legs: FlightLeg[];
    refund: FlightRefund | null;
}

export interface FlightTicket {
    pnr: string | null;
    type: FlightType;
    airlineNumber: string | null;
    boardingPassUrl: string | null;
    verification?: FlightTicketVerification | null;
}

export interface FlightTicketVerification {
    confirmed: boolean;
    url: string | null;
    validUntil: string | null;
}

export interface FlightRefund {
    date: string | null;
    pending: boolean;
}

export interface FlightPax extends Pax {
    ticketNumber: string | null;
    baggages: FlightBaggage[] | null;
    seats: FlightSeat[] | null;
}

export interface FlightBaggage {
    type: FlightBaggageType;
    description: string | null;
    pieces: number | null;
    weight: number | null;
}

export enum FlightBaggageType {
    Unknown = 'Unknown',
    CheckedBag = 'CheckedBag',
    CabinBag = 'CabinBag',
    SmallBag = 'SmallBag',
}

export interface FlightSeat {
    description: string;
    row: number;
    column: string;
    departureAirportCode: string;
    arrivalAirportCode: string;
}

export interface FlightLeg {
    platingCarrier: string | null;
    platingCarrierLogo: string | null;
    platingCarrierName: string | null;
    segments: FlightSegment[];
    travelTime: string | null;
}

export interface FlightSegment {
    arrivalAirportCode: string | null;
    arrivalAirportName: string | null;
    arrivalCityName: string | null;
    arrivalCountryName: string | null;
    arrivalDate: string | null;
    cabinClassType: CabinClass;
    departureAirportCode: string | null;
    departureAirportName: string | null;
    departureCityName: string | null;
    departureCountryName: string | null;
    departureDate: string | null;
    flightNumber: string | null;
    flightTime: string | null;
    marketingCarrier: string | null;
    marketingCarrierName: string | null;
    marketingCarrierLogo: string | null;
    operatingCarrier: string | null;
    operatingCarrierName: string | null;
    operatingCarrierLogo: string | null;
    stopOverTime: string | null;
    hasTechnicalStop?: boolean | null;
}

export enum FlightType {
    LCC = 'LCC',
    TravelFusion = 'TravelFusion',
    Other = 'Other',
    Ryanair = 'Ryanair',
}

export enum CabinClass {
    Unknown = 'Unknown',
    Economy = 'Economy',
    Premium = 'Premium',
    Business = 'Business',
    First = 'First',
}

export interface ActivityProduct {
    type: ProductType.Activity;
    placeCode: string;
    placeName: string;
    date: string;
    description: string;
    categoryDescription: string | null;
    imageUrl: string;
    voucherAvailability?: string | null;
    tickets: ActivityTicket[];
}

export interface ActivityTicket {
    bookingReference: string | null;
    adults: number;
    children: number;
    productId: number;
}

export interface TransferProduct {
    type: ProductType.Transfer;
    productId: number;
    flightNo: string | null;
    from: TransferLocation;
    to: TransferLocation;
    pickUpTime: string | null;
    journeyType: TransferJourneyType;
    shieldText: string | null;
    partner: TransferPartner;
    vehicle: TransferVehicle;
    passengers: TransferPassengers;
    luggage: number;
    duration: number;
    conditions: string | null;
    disclaimer: TransferDisclaimer;
    providerVoucherUrl: string | null;
    isSharedTransfer?: boolean;
}

export interface TransferLocation {
    dateTime: string | null;
    address: string | null;
    iataInfo: IataInfo | null;
}

interface IataInfo {
    code: string | null;
    label: string | null;
    labelShort: string | null;
    city: string | null;
    country: string | null;
}

interface TransferPassengers {
    adults: number;
    children: number;
    infants: number;
}

interface TransferPartner {
    name: string | null;
    image: string | null;
}

interface TransferVehicle {
    image: string | null;
    model: string | null;
    serviceType: string | null;
    transferCategory: string | null;
}

interface TransferDisclaimer {
    title: string | null;
    description: string | null;
}

export interface TravelInsuranceProduct {
    type: ProductType.GenericProduct;
    description: string;
    name: string;
}

export interface CancellationInsuranceProduct {
    type: ProductType.CancellationInsurance;
    description: string;
    name: string;
}

export interface BaggageInsuranceProduct {
    type: ProductType.BaggageInsurance;
    description: string;
    name: string;
}

export interface ServicePackageProduct {
    type:
        | ProductType.ServicePackageBasic
        | ProductType.ServicePackagePremium
        | ProductType.ServicePackageOld;
    description: string;
    name: string;
}

export interface ServicePackageAccommodationProduct {
    type: ProductType.ServicePackageAccommodation;
    description: string;
    name: string;
}

export interface CustomBaggageProduct {
    type: ProductType.CustomBaggage;
    description: string;
}

export interface CustomSeatingProduct {
    type: ProductType.CustomSeating;
    description: string;
}

export interface BookingCancellationPolicy {
    productId: number;
    fromDate: Date | string;
    price: number;
    currency: string;
}

export enum CouponPlacement {
    HotelWidget = 'hotel_widget',
    TripWidget = 'trip_widget',
    Main = 'main',
}
